/**
 * Conversation_Store boot-time adapter selector (Requirement 12.2).
 *
 * The Chat_Client boot sequence calls `openConversationStore()` once.
 * The selector prefers the Dexie-backed adapter (`./dexie.ts`) so
 * messages, profile, preferences, and the companion name persist across
 * sessions. When `openDexieStore()` returns `null` — IndexedDB is
 * unavailable in private browsing, embedded WebViews, or browsers
 * where the user has disabled storage — the selector swaps in the
 * in-memory adapter (`./inMemory.ts`) so the rest of the app continues
 * to work and the boot sequence surfaces the persistence-unavailable
 * banner via the `inMemoryFallback` flag.
 *
 * Pure barrel + a single async factory: no module-level state, no I/O
 * beyond what the underlying adapters do, no side effects on import.
 */

import { openDexieStore } from './dexie';
import { createInMemoryStore } from './inMemory';
import type { OpenStoreResult } from './types';

/**
 * Open the Conversation_Store, preferring the Dexie adapter and
 * falling back to the in-memory adapter when IndexedDB is unavailable
 * (Requirement 12.2). The boot sequence inspects `inMemoryFallback` to
 * decide whether to surface the persistence-unavailable banner.
 */
export async function openConversationStore(): Promise<OpenStoreResult> {
  const dexie = await openDexieStore();
  if (dexie !== null) {
    return { store: dexie, inMemoryFallback: false };
  }
  return { store: createInMemoryStore(), inMemoryFallback: true };
}

// Re-exports so consumers don't have to know the file layout.
export type {
  ConversationStore,
  OpenStoreResult,
  QueuedMessageRow,
  StoreResult,
} from './types';
export { withRetry, RETRY_DELAYS_MS, MAX_RETRY_ATTEMPTS } from './retry';
export { validateExportBundle } from './validator';
